/**
 * THE MODULE CONTRACT.
 *
 * Every feature of the platform is a module: a folder under modules/ whose
 * manifest tells the platform everything it needs. The platform reads manifests
 * to build the menu, mount the API, run migrations, register jobs and widgets,
 * and decide who may see what. Nothing is wired by hand.
 *
 * A module has two halves that share one manifest:
 *   - src/manifest.ts  identity, permissions, nav (imported by server AND web)
 *   - src/server.ts    the API and migrations (server only)
 *   - src/web.tsx      the pages and widgets (browser only)
 */
import type { z } from "zod";

/** Platform-fixed module accents (see the Heritage Plus design system). */
export type ModuleAccent =
  | "shop" | "fuel" | "wetstock" | "deliveries" | "cash" | "promotions" | "maintenance" | "people" | "brand";

export type ModuleTier = "core" | "addon";

/** How a site is run. Modules, menu entries and setup items can apply to one model only. */
export type OperatingModel = "commission" | "company" | "operator";
/**
 *   commission : run by a commission operator (their business; commission on categories, rent, charges)
 *   company    : run directly by the company (managers on payroll)
 *   operator   : operator-managed — the company owns stock, sales and waste; an operator runs the site for a
 *                weekly management fee and staffs it, signing off like a store manager would
 */
export const OPERATING_MODELS: { id: OperatingModel; label: string; short: string }[] = [
  { id: "commission", label: "Commission operator", short: "Commission" },
  { id: "operator", label: "Operator managed (weekly fee)", short: "Operator managed" },
  { id: "company", label: "Company managed", short: "Company" },
];

/**
 * What a site has besides fuel and a shop, answered once per site in Setup ("Which services does each site have?").
 * Setup steps with `when: { services }` only show at sites that have one of them.
 */
export const SITE_SERVICES = [
  { id: "ready2wash", label: "Ready2Wash car wash" },
  { id: "nayax", label: "Nayax card readers (car wash, vacuums)" },
  { id: "lynked", label: "Lynked loyalty" },
  { id: "costa", label: "Costa Express" },
  { id: "paypoint", label: "PayPoint" },
  { id: "atm", label: "Cash machine" },
] as const;
export type SiteService = (typeof SITE_SERVICES)[number]["id"];

/** Show/require something only for sites that match. Omitted = all sites. */
export interface SiteCondition {
  operatingModel?: OperatingModel[];
  /**
   * Setup items only (menus ignore it for now): sites that have at least one of these services. A site whose services
   * haven't been answered yet waits, like a site with no operating model.
   */
  services?: SiteService[];
  /**
   * Setup items only: sites whose Site details answer is one of these (e.g. `{ wholesaler: ["nisa", "coop"] }`: only
   * sites supplied by Co-op Wholesale need a Co-op login; Jamie, 26/10/26). A site that hasn't answered it yet waits.
   */
  siteDetails?: Partial<Record<"wholesaler" | "epos_system" | "fuel", string[]>>;
  /**
   * true: only sites that sell fuel (29/10/26, for companies with shops that don't). A site sells fuel when Site
   * details says so, else when it has fuel set up, tanks, fuel deliveries or litres sold (the sites module's
   * `fuelSites`). Menus show while any of the person's sites sells fuel; set-up items ask only those sites.
   */
  fuel?: boolean;
  /**
   * true: company-wide set-up items only, asked only of a company with a head office (an active site whose kind is
   * `head_office`; 29/10/26, docs/readiness.md: never assume one). Menus use a module feature instead (e.g. Products'
   * `headOfficeImports`, false with no head office).
   */
  headOffice?: boolean;
}

export interface NavEntry {
  label: string;
  /** Path inside the app, starting with the module's base path. */
  path: string;
  /** Permission key (without the module prefix) the user needs to see this entry. */
  permission: string;
  icon?: string;
  /** Lower sorts first. */
  order?: number;
  /** Only shown when the user has at least one matching site. */
  when?: SiteCondition;
  /**
   * Which menu section it lives in (see NAV_SECTIONS). The old ids ("home", "me", "today", "money",
   * "sites") are still accepted while modules move over, and mapped by `navSectionOf`.
   */
  section?: NavSection | LegacyNavSection;
  /**
   * "settings" rows are listed under Settings in their section (and in Settings → Section settings); "page" rows are
   * everyday pages. Defaults to "settings" for the `settings` permission and settings-like pages, else "page".
   */
  kind?: "page" | "settings";
  /** One line shown under the page's name on its section's home page. */
  blurb?: string;
  /** Managing pages (only for people with that permission) are grouped under "Manage". Defaults from the permission. */
  admin?: boolean;
  /**
   * The tab it sits under in its section's tab row (at most six tabs a section, icon + word). Pages sharing a tab
   * show as small links under it. E.g. Price changes and Price checks both `tab: "Prices"`. Unset: the platform's
   * default grouping (apps/web/src/nav.ts), else its own tab.
   */
  tab?: string;
  /** The tab's icon when this entry leads its tab (defaults to the entry's icon). */
  tabIcon?: string;
  /**
   * Shown only when the company has at least one of these modules too (modules sold on their own, 26/10/26): e.g.
   * Reports' "Fuel sold" needs `["fuel", "wetstock"]`.
   */
  requires?: string[];
  /** Shown only while this module's own yes / no switch is on (its `features` hook; e.g. fuel's "margins"). */
  feature?: string;
  /**
   * Part of moving from the old Tankerford Cloud (29/10/26): shown only while the company's old Cloud link is on
   * (`ctx.oldCloud()`). Never seen by any other company, and gone for everyone once the link is retired.
   */
  oldCloud?: boolean;
}
/**
 * The company app's menu sections, in order (Jamie, 04/11/26: "too busy and confusing … cleaner, crisper, easier").
 * A module you buy adds its pages to the menu, mostly as its own section (Maintenance, Compliance, Audits), and every
 * report sits in the section it's about: there is no Reports section. My tasks sits under Dashboard with its count.
 * "operations" is shown as Sites (sites, ratings, screens); the id stays so remembered pages and links keep working.
 */
export type NavSection = "dashboard" | "tasks" | "fuel" | "shop" | "cash" | "maintenance" | "compliance" | "audits" | "people" | "operations" | "settings" | "bridge";
/** Section ids from before the menu was simplified (14/10/26). Mapped to the new ones; don't use in new code. */
export type LegacyNavSection = "home" | "me" | "today" | "money" | "sites" | "reports";

export const NAV_SECTIONS: { id: NavSection; label: string; icon: string }[] = [
  { id: "dashboard", label: "Dashboard", icon: "home" },
  { id: "tasks", label: "My tasks", icon: "tasks" },
  { id: "fuel", label: "Fuel", icon: "fuel" },
  { id: "shop", label: "Shop", icon: "basket" },
  { id: "cash", label: "Cash Office", icon: "pound" },
  { id: "maintenance", label: "Maintenance", icon: "wrench" },
  { id: "compliance", label: "Compliance", icon: "shield" },
  { id: "audits", label: "Audits", icon: "check-circle" },
  { id: "people", label: "People", icon: "users" },
  { id: "operations", label: "Sites", icon: "pin" },
  { id: "settings", label: "Settings", icon: "settings" },
  // The Nozl Bridge (Jamie, 29/10/26): its own place at the foot of the menu, for the extra uploads to the head office
  // PC (each page's own send buttons stay on the page). Only shown when the company has head office imports on.
  { id: "bridge", label: "Nozl Bridge", icon: "upload" },
];
const NEW_IDS = new Set<string>(NAV_SECTIONS.map((s) => s.id));
/** Where a module's old-style entries go until the module sets a new section itself. */
const LEGACY_BY_MODULE: Record<string, NavSection> = { budgets: "dashboard", insights: "dashboard", bonuses: "people", loyalty: "shop", "rent-review": "cash", hr: "people", learning: "people", chat: "people" };
const LEGACY: Record<LegacyNavSection, NavSection> = { home: "dashboard", me: "people", today: "operations", money: "cash", sites: "operations", reports: "dashboard" };

/** The section a menu entry belongs to (new id as given, an old id mapped, or by its module). */
export function navSectionOf(moduleId: string, entry: Pick<NavEntry, "section">): NavSection {
  const s = entry.section;
  if (s && NEW_IDS.has(s)) return s as NavSection;
  if (s === "me" || s === "money" || s === "today" || s === "sites" || s === "home" || s === "reports") return (s !== "home" && LEGACY_BY_MODULE[moduleId]) || LEGACY[s];
  return LEGACY_BY_MODULE[moduleId] ?? "operations";
}

/** Whether a menu entry is a settings row (explicit `kind`, else from its permission and name). */
export function navKindOf(entry: Pick<NavEntry, "kind" | "permission" | "label">): "page" | "settings" {
  if (entry.kind) return entry.kind;
  if (entry.permission === "settings") return "settings";
  return /(settings|setup|configuration|builder)$/i.test(entry.label) || /^manage /i.test(entry.label) ? "settings" : "page";
}

export interface ModuleManifest {
  /** kebab-case, unique, never changes once released. Also the URL segment and DB table prefix. */
  id: string;
  /**
   * Uploads (/api/files with this module): `zip: true` takes a whole .zip (up to 200 MB, stored as application/zip)
   * for the module to open on the server; other files stay 50 MB (27/10/26).
   */
  uploads?: { zip?: boolean };
  name: string;
  version: string;
  tier: ModuleTier;
  accent: ModuleAccent;
  description: string;
  dependsOn?: string[];
  /** Where its pages live in the app. Defaults to /<id>. The dashboard uses "/". */
  basePath?: string;
  /** Which kinds of site this module is for. Omitted = every site. (e.g. Commission: ["commission"]) */
  appliesTo?: SiteCondition;
  /** key -> human description. Full permission ids are `<module id>.<key>`. */
  permissions: Record<string, string>;
  nav: NavEntry[];
  /**
   * Sensible starting permissions for the built-in levels, applied when the module is switched on for a
   * company (Company admin already has everything). Companies can change them afterwards.
   */
  grants?: Partial<Record<"Head office" | "Area manager" | "Site manager" | "Supervisor" | "Staff", string[]>>;
  /**
   * What this module needs to know before it can work for a company or a site.
   * The onboarding wizard is built from these: no module ever hand-builds a setup screen.
   * Each item is ticked green only when its server-side check passes.
   */
  setup?: SetupItem[];
  /**
   * THE SET-UP TOOL'S QUESTIONS (29/10/26, docs/onboarding.md "The set-up tool"): a few plain multiple-choice questions
   * that decide this module's settings for a company, asked on the module's own page of the set-up tool (and again when
   * the module is added to a company later). Each answer is suggested from the company profile by the server half's
   * `questions.suggest`, and written into the module's settings by `questions.apply`. Keep it to what a manager can
   * answer in a few seconds; logins and data feeds are `setup` items, not questions.
   */
  questions?: ModuleQuestion[];
  /**
   * Outside systems this module connects to, offered as tiles with a logo and a switch in the set-up tool (29/10/26).
   * Switched off, their `setup` steps leave the checklist; a per-site one (`service`) asks which sites have it.
   * Logos are uploaded in the console (Connections → Logos), so a new integration needs no picture in the code.
   */
  integrations?: IntegrationDecl[];
  /**
   * Kinds of task this module raises (see Tasks). Keys are local ("sign-off"); the full key is "<module>.<key>".
   * Companies can change the level, who gets it and how it clears on Tasks → Task types.
   */
  tasks?: TaskTypeDecl[];
  /** Cards this module offers for the home dashboard (see Dashboard). Data comes from the server half's `widgets`. */
  widgets?: WidgetDecl[];
}

/* ---------------- dashboard contract ---------------- */

export interface WidgetDecl {
  key: string;
  title: string;
  /** One line for the "Add cards" list. */
  description: string;
  /** Where it's listed when customising, e.g. "Sales", "Fuel", "Jobs". */
  group: string;
  /** Width on a desktop: 1 = a third, 2 = two thirds, 3 = full width. Phones always show one per row. */
  size?: 1 | 2 | 3;
  /** Permission key in this module needed to see it. */
  permission: string;
  /** Shown to people who haven't customised their dashboard. */
  defaultOn?: boolean;
  /** Lower sorts first in the default layout. */
  order?: number;
  /** Live right now (tanks, prices, deliveries) rather than for the chosen period: shown under "Live now", refreshed every minute. */
  live?: boolean;
  /** Offered only when the company has at least one of these modules too (see NavEntry.requires). */
  requires?: string[];
  /** Offered only while this module's own yes / no switch is on (see NavEntry.feature). */
  feature?: string;
  /**
   * Offered only to a company with a site that fits (30/10/26), e.g. `{ operatingModel: ["commission"] }` for a
   * commission invoices card. The module's `appliesTo` counts too. A site not answered yet fits.
   */
  when?: SiteCondition;
}

export type WidgetUnit = "gbp" | "litres" | "count" | "pct" | "hours" | "ppl";
export interface WidgetQuery {
  /** The sites to show (already limited to what the person may see). */
  sites: string[];
  from: string; to: string;
  /** The comparison period (same days last week, last month…). */
  prevFrom: string; prevTo: string;
  label: string;
  userId: string;
}
/** What a card shows. The dashboard draws it; modules only supply numbers. */
export type WidgetData =
  | { kind: "stat"; value: number | null; unit: WidgetUnit; prev?: number | null; good?: "up" | "down"; hint?: string; trend?: number[]; link?: string;
      /** One row per site (label = site code); `extra` lines up with `extraCols` (e.g. margin %). */
      rows?: { label: string; value: number | null; prev?: number | null; link?: string; extra?: (number | null)[] }[]; extraCols?: { label: string; unit: WidgetUnit }[] }
  | { kind: "bars"; unit: WidgetUnit; items: { label: string; value: number; prev?: number | null }[]; total?: number | null; link?: string }
  | { kind: "columns"; unit: WidgetUnit; series: { label: string; value: number; prev?: number | null }[]; link?: string }
  | { kind: "list"; items: { title: string; detail?: string; status?: "critical" | "warning" | "info" | "success"; link?: string; tag?: string }[]; counts?: { label: string; value: number; tone?: "critical" | "warning" | "info" | "success" }[]; empty?: string; link?: string }
  | { kind: "table"; cols: string[]; rows: (string | number | null)[][]; units?: (WidgetUnit | null)[]; link?: string;
      /** Draw each value on its own coloured chip: r red, o orange, y yellow, g green, x solid dark red (e.g. off sale). */
      chips?: boolean; tones?: (ChipTone | null)[][]; subs?: (string | null)[][]; legend?: { tone: ChipTone; label: string }[]; note?: string }
  | { kind: "tiles"; items: { label: string; value: number | null; unit: WidgetUnit; prev?: number | null; good?: "up" | "down"; hint?: string; link?: string; tone?: "critical" | "warning" | "success" | "info" }[] }
  | { kind: "empty"; message: string; link?: string }
  /** Each site's weather now and for the next few days, with frost, wind, heat and rain flagged. */
  | { kind: "weather"; days: { day: string; label: string }[]; link?: string; missing?: number;
      sites: { code: string; name: string; now: { temp: number; code: number; label: string } | null; days: (WeatherDay | null)[]; flag?: "Frost" | "Wind" | "Hot" | "Wet" | null }[] }
  /** Days of fuel left, as coloured chips per site and grade (red under a day, orange 1–2, yellow 2–3, green after). */
  | { kind: "cover"; link?: string; counts?: { label: string; value: number; tone?: "critical" | "warning" | "info" | "success" }[];
      sites: { code: string; name: string; link?: string; lanes: { grade: string; label?: string; days: number | null; litres?: number | null; offSale?: boolean; offFor?: string | null; estimate?: boolean;
        next?: string | null; nextAt?: string | null; nextLitres?: number | null }[] }[];
      order?: { title: string; detail?: string; link?: string; site?: string; grade?: string; when?: string; late?: boolean }[] }
  /**
   * Each site's food hygiene rating and Google stars on one line (Sites, 26/10/26): the FSA's own badge when its file
   * is there (`badge`, a same-origin address), else the rating as a small tile; the stars drawn; any newest low review.
   */
  | { kind: "ratings"; link?: string; counts?: { label: string; value: number; tone?: "critical" | "warning" | "info" | "success" }[];
      sites: { code: string; name: string; link?: string; badge: string | null; fsa: string | null; fsaText: string; fsaTone: "ok" | "warn" | "bad" | "info"; stars: number | null; reviews: number | null }[];
      items?: { title: string; detail?: string; status?: "critical" | "warning" | "info" | "success"; link?: string; tag?: string }[] };
export type ChipTone = "r" | "o" | "y" | "g" | "x";
/** One day of a site's forecast (WMO weather code, °C, chance of rain %, wind mph). */
export interface WeatherDay { code: number; label: string; max: number; min: number; rain: number | null; wind: number | null }

/* ---------------- tasks contract ---------------- */

export type TaskLevel = "info" | "action" | "urgent";
/** How a kind of task clears: someone signs it off; it clears itself when fixed; acknowledge until it changes; acknowledge for N hours. */
export type TaskClearMode = "sign_off" | "fix" | "ack_change" | "ack_hours";

export interface TaskTypeDecl {
  key: string;
  title: string;
  /** One line for the Task types page: what raises it and when it clears. */
  description: string;
  level?: TaskLevel;
  /** "sites" (default): the people at the task's site, plus anyone routed. "routed": only routed people/groups. */
  audience?: "sites" | "routed";
  clear?: TaskClearMode;
  ackHours?: number;
  /** Where "Open" goes, e.g. "/cash-office/sign-off". A task can carry its own link. */
  link?: string;
  icon?: string;
  sort?: number;
  /** Send a notification when one arrives (default true). Turn off where the module already tells people itself. */
  notify?: boolean;
  /**
   * Listed (Tasks → Task types, Insights alerts) only for a company with a site that fits (30/10/26), e.g.
   * `{ operatingModel: ["commission"] }`. The module's `appliesTo` counts too. A site not answered yet fits.
   */
  when?: SiteCondition;
}

/** Shown on the task and the top bar: a tag, up to 3 number tiles, and an optional small table. */
export interface TaskFacts {
  tag?: string;
  tiles?: { value: string | number; label: string }[];
  cols?: string[];
  rows?: (string | number)[][];
  note?: string;
}

export interface RaiseTask {
  /** The module's local task type key (declared in its manifest). */
  type: string;
  /** Stable identity within the type: the same key updates the same task (e.g. the site code, "job:123"). */
  key: string;
  /** Site it's about; null/omitted = company-wide (goes to routed people, or task managers). */
  site?: string | null;
  /** For one person only (their user id). */
  user?: string | null;
  /** Extra people/groups for this task only. */
  recipients?: { kind: "user" | "group"; ref: string }[];
  title: string;
  body?: string;
  /** Defaults to the type's level (or the site's override). */
  level?: TaskLevel;
  facts?: TaskFacts;
  link?: string;
  /** Due date (YYYY-MM-DD): overdue tasks show as urgent. */
  due?: string | null;
  /** Never raise this key again once it has been signed off (e.g. "order the P11 promo lines"). */
  once?: boolean;
  /** How many things this one task stands for (e.g. 37 documents waiting to be filed): the menu badge adds these up. Default 1. */
  count?: number;
}

/**
 * TASKS — every module raises tasks through here, so they all land in one list per person, the menu badge and the
 * top bar, and clear when the thing is done. Raising the same type + key again updates the task (or brings it back
 * if it was signed off, unless `once`). Switched-off types, sites switched off for a type and dismissed tasks are
 * skipped.
 */
export interface Tasks {
  /** Raise or update a task. Returns its id, or null if it's switched off, dismissed or already done (`once`). */
  raise(t: RaiseTask): Promise<number | null>;
  /** The thing is done: clear the task (it's recorded as "fixed itself"). */
  clear(type: string, key: string): Promise<void>;
  /**
   * Make this type's live tasks exactly `tasks`: raise/update each, clear every other active task of the type
   * (within `site` if given). The easy way to keep a type in step from a reconcile.
   */
  sync(type: string, tasks: Omit<RaiseTask, "type">[], scope?: { site?: string }): Promise<void>;
  /** Has this type + key ever been signed off? */
  completedFor(type: string, key: string): Promise<boolean>;
}

/* ---------------- onboarding / setup contract ---------------- */

export type SetupFieldType =
  | "text" | "number" | "select" | "boolean" | "email" | "url"
  /** Stored encrypted in the tenant's secrets vault; never sent back to the browser once saved. */
  | "secret"
  /** Mon–Sun open/close times, with "24h" and "closed" options. */
  | "opening-hours";

export interface SetupField {
  key: string;
  label: string;
  type: SetupFieldType;
  required?: boolean;
  help?: string;
  options?: { value: string; label: string }[];
  placeholder?: string;
  /**
   * For the "every site at once" wizard and the CSV: `perSite` = always different per site (an address, an account
   * number), so the wizard never offers one answer for all; `shared` = a text field that's usually the same everywhere.
   * Without either, choices, yes/no, numbers and opening hours are offered once for every site; free text isn't.
   */
  perSite?: boolean;
  shared?: boolean;
}

export interface SetupItem {
  /** Unique within the module, e.g. "sm365-credentials". */
  id: string;
  title: string;
  /** "tenant" = once per company; "site" = once per forecourt. */
  scope: "tenant" | "site";
  /** Why we need it, in plain words, shown in the wizard. */
  why: string;
  fields: SetupField[];
  /** Must the module have this green before it can be switched on? Defaults to true. */
  blocking?: boolean;
  /** Other setup items (`<module>/<item>`) that must be green first, e.g. credentials before site matching. */
  after?: string[];
  /** Site-scoped items only: a page that does every site at once (e.g. link every site to Fuel Finder in one list). */
  bulk?: { label: string; path: string; blurb?: string };
  /** Site-scoped items only: which sites need it (e.g. commission rates only for commission sites). */
  when?: SiteCondition;
  /**
   * Site-scoped items only: ask this of head offices too. Left out by default (a head office has no forecourt, till
   * or operator to set up).
   */
  headOffice?: boolean;
  /**
   * Company-wide items: the Nozl mailbox this step is about (its key). Setup shows the address to forward the emails
   * to, with a copy button, and when the first and last arrived; the step's check says whether forwarding works.
   */
  mailbox?: string;
  /** Any item: the module page where this is really done, e.g. { label: "Open Maintenance → Contractors", path: "/maintenance/settings#contractors" }. */
  link?: { label: string; path: string };
}

export type CheckStatus = "green" | "amber" | "red";
export interface CheckResult {
  status: CheckStatus;
  /** One line a site manager understands, e.g. "Connected — 11 tanks found". */
  detail: string;
}

/** An email forwarded to a company's Nozl address (see Mailbox). */
export interface MailboxMessage {
  id: string;
  /** Increases with every email kept for the company: keep the last one handled and ask for the ones after it. */
  seq: number;
  key: string;
  address: string;
  messageId: string | null;
  from: string;
  subject: string;
  sentAt: string | null;
  receivedAt: string;
  size: number;
}

/**
 * A NOZL MAILBOX (19/10/26): instead of a login to the company's own mailbox, the company forwards the emails to its
 * own address at Nozl, one per purpose (`key`, e.g. "deliveries", "nayax"), made the first time it's asked for.
 */
export interface Mailbox {
  /** The address to forward to (made on first ask). Null while Nozl's inbound domain isn't set up. */
  address(key: string): Promise<string | null>;
  /** The address, how many emails have arrived and when the first and last came (for "forwarding works"). */
  status(key: string): Promise<{ address: string | null; count: number; first: string | null; last: string | null;
    /** Gmail's forwarding confirmation code, while forwarding is being set up (the emails themselves are never shown). */
    confirmation?: { code?: string; /** Google's own link that confirms it, when the email has one. */ link?: string; receivedAt: string } }>;
  /** Emails that arrived, oldest first, after `afterSeq` (default: from the start), up to `limit` (50, 500 at most). */
  list(key: string, opts?: { afterSeq?: number; limit?: number }): Promise<MailboxMessage[]>;
  /** One email with its whole source (RFC 822), to parse with mailparser's simpleParser. Null if it isn't this module's. */
  read(id: string): Promise<(MailboxMessage & { source: Uint8Array }) | null>;
}

export interface SetupContext {
  tenant: { id: string; slug: string; name: string };
  /** Set for site-scoped items. */
  site?: { code: string; name: string };
  db: TenantDb;
  /** Saved non-secret values for this item. */
  values: Record<string, unknown>;
  /** Read a saved secret for this item (server side only). */
  secret(key: string): Promise<string | null>;
  /** This module's Nozl mailbox, e.g. to check forwarding works ("first email arrived 19/10/26"). Always set by Setup. */
  mailbox?: Pick<Mailbox, "status">;
}

export interface SetupHandlers {
  /** Prove the item works: log in, call the API, find the file. Runs on save, on demand and nightly. */
  check(ctx: SetupContext): Promise<CheckResult>;
  /**
   * Optional: once an earlier item is green, suggest values so the user only confirms
   * (e.g. list SM365 sites so they can be matched to ours with one click).
   */
  discover?(ctx: SetupContext): Promise<Record<string, unknown>[]>;
  /** Optional: copy saved values into the module's own tables (e.g. a site's EPOS system). Runs on every save. */
  apply?(ctx: SetupContext & { user: { id: string } }): Promise<void>;
}

export function defineManifest<const M extends ModuleManifest>(m: M): M {
  if (!/^[a-z][a-z0-9-]{1,40}$/.test(m.id)) throw new Error(`Module id "${m.id}" must be kebab-case`);
  for (const n of m.nav) {
    if (!(n.permission in m.permissions)) throw new Error(`${m.id}: nav "${n.label}" uses unknown permission "${n.permission}"`);
  }
  for (const [level, perms] of Object.entries(m.grants ?? {})) {
    for (const p of perms ?? []) if (!(p in m.permissions)) throw new Error(`${m.id}: grant for ${level} uses unknown permission "${p}"`);
  }
  return m;
}

export const permissionId = (moduleId: string, key: string) => `${moduleId}.${key}`;

/* ---------------- server half ---------------- */

export interface Queryable {
  query<T extends object = Record<string, unknown>>(sql: string, params?: unknown[]): Promise<T[]>;
}
export interface TenantDb extends Queryable {
  transaction<T>(fn: (tx: Queryable) => Promise<T>): Promise<T>;
}

/* ---------------- shared services every module gets ---------------- */

/** One figure the platform keeps per site per day (from the tills, imports or typed in). */
export interface FigureKey { key: string; name: string; group: "shop" | "department" | "concession" | "fuel" | "people" | "other"; unit: "gbp" | "litres" | "hours" | "count" | "pct"; good: "up" | "down" }
export interface FigureRow { site: string; key: string; day: string; amount: number }

/**
 * THE DAILY FIGURES STORE — every module reads the same numbers (shop sales, departments, concessions, fuel litres
 * by grade, waste, wages…), so targets, commission, fees and bonuses can never disagree. The EPOS feed writes them;
 * until then they're imported on the Figures page.
 */
export interface Figures {
  catalogue(): Promise<FigureKey[]>;
  /** Daily rows for these sites and keys between two dates (inclusive). */
  daily(sites: string[], keys: string[], from: string, to: string): Promise<FigureRow[]>;
  /** Totals per site and key between two dates. */
  totals(sites: string[], keys: string[], from: string, to: string): Promise<{ site: string; key: string; amount: number }[]>;
  /** Latest day with any figure for each site. */
  lastDay(sites: string[]): Promise<Record<string, string | null>>;
  /**
   * Write one site-day's figures from a source (e.g. "coa-pdf"). Keys this source wrote before for that site-day
   * but not now are removed, so a reprocess never leaves stale numbers. Other sources' figures are untouched
   * unless `replace` is true, in which case this source takes the key over. Unknown keys throw.
   */
  put(site: string, day: string, values: Record<string, number>, source: string, opts?: { replace?: boolean }): Promise<void>;
}

/** Encrypted values for this module (bank details, passwords). Never sent to the browser. */
export interface ModuleSecrets {
  get(item: string, key: string, site?: string): Promise<string | null>;
  set(item: string, key: string, value: string | null, site?: string): Promise<void>;
  /** Which keys are saved (so the page can say "saved" without revealing them). */
  saved(item: string, site?: string): Promise<string[]>;
}

/* ---------------- market data (shared by every company) ---------------- */

export interface PlattsRow { brand: string; day: string; grade: string; platts_ppl: number }
export interface FuelStation {
  node_id: string; name: string; brand: string; address: string; town: string; postcode: string;
  lat: number | null; lng: number | null; motorway: boolean; supermarket: boolean; temp_closed: boolean; perm_closed: boolean;
  /** Only the grades the station reports now: { unleaded: { p: 139.9, at: "…" } }. */
  prices: Record<string, { p: number; at: string | null }>;
  price_updated_at: string | null; fetched_at: string;
}
/**
 * MARKET DATA — the same for every company, kept once by Nozl: the PLATTS fuel benchmark (keyed in centrally), fuel
 * duty by date, and the government Fuel Finder list of every UK forecourt and its prices.
 */
/** Today and the next days' weather at a place (Open-Meteo), in UK terms: °C, mph, % chance of rain. */
export interface Forecast {
  now: { temp: number; code: number; label: string } | null;
  days: { day: string; code: number; label: string; max: number; min: number; rain_pct: number | null; wind_mph: number | null }[];
  fetched_at: string;
}

export interface Market {
  /** The forecast at a place, shared by every company and refreshed at most every 3 hours. Null if it can't be fetched. */
  weather(lat: number, lng: number): Promise<Forecast | null>;
  /** Nozl's own Fuel Finder read client for the shared feed (platform credential), when one is set. */
  fuelFinderClient(): { id: string; secret: string } | null;
  platts(brand: string, from: string, to: string): Promise<PlattsRow[]>;
  /** Duty in force on a day for a grade (pence per litre). */
  duty(day: string, grade: string): Promise<{ duty: number; from: string }>;
  stations(nodeIds: string[]): Promise<FuelStation[]>;
  stationsNear(lat: number, lng: number, radiusM: number): Promise<(FuelStation & { distance_m: number })[]>;
  /** Find stations by name, brand, town or postcode (for matching a site to Fuel Finder or adding a competitor). */
  searchStations(q: string, limit?: number): Promise<FuelStation[]>;
  /** Save stations fetched from Fuel Finder (only the fields given are changed; prices replace what was there). */
  saveStations(rows: (Partial<FuelStation> & { node_id: string })[]): Promise<number>;
  /** One company refreshes a shared feed at a time: claim `key` if it last ran more than `minutes` ago. */
  claim(key: string, minutes: number): Promise<{ claimed: boolean; ranAt: string | null; okAt: string | null; info: Record<string, unknown> }>;
  done(key: string, ok: boolean, info?: Record<string, unknown>): Promise<void>;
  state(key: string): Promise<{ ranAt: string | null; okAt: string | null; info: Record<string, unknown> }>;
}

/* ---------------- AI reading ---------------- */

export type AiPart = { type: "text"; text: string } | { type: "image"; mime: string; data: Uint8Array } | { type: "pdf"; data: Uint8Array };
/** Reading documents and photos (delivery tickets, supplier invoices) with a model. JSON in, JSON out. */
export interface Ai {
  /** Is a model configured for this platform? */
  available(): boolean;
  /** "fast" is cheap and quick; "strong" is the careful second look. Throws if the answer isn't JSON. */
  /**
   * `purpose`: a few words for the usage report (e.g. "delivery ticket"). Only the parts given are sent: nothing about
   * the company is added, and one call never mixes companies. Each call counts against this company's AI budget (the
   * console's limits); a failure throws one short plain sentence a page can show as it is.
   */
  json<T = unknown>(req: { system: string; parts: AiPart[]; model?: "fast" | "strong"; maxTokens?: number; timeoutMs?: number; purpose?: string }): Promise<AiAnswer<T>>;
}

/** One AI answer, with what it cost (the same figures the usage report keeps; 27/10/26). */
export interface AiAnswer<T> { data: T; model: string; /** Always there from the platform; optional so test stand-ins needn't give them. */ inputTokens?: number; outputTokens?: number; costGbp?: number }

/**
 * Why an AI call failed (27/10/26, Modules): `reason` tells apart what a page might do about it.
 *  - "refused": Anthropic refused the request (e.g. a PDF it can't take); `status` is Anthropic's (400…), nothing charged.
 *  - "cut-short": the answer hit maxTokens (stop_reason "max_tokens"): charged; ask for more tokens or less.
 *  - "unreadable": an answer came back but wasn't the JSON asked for: charged.
 *  - "limit": this company's budget, day or minute limit (no call made). "unavailable": not set up or not reachable.
 * `message` is always one plain sentence a page can show as it is.
 */
export class AiError extends Error {
  constructor(message: string, public readonly reason: "refused" | "cut-short" | "unreadable" | "limit" | "unavailable",
    public readonly status: number | null, public readonly inputTokens = 0, public readonly outputTokens = 0, public readonly costGbp = 0) {
    super(message);
    this.name = "AiError";
  }
}

/* ---------------- captcha answering (platform-wide 2captcha) ---------------- */

export type CaptchaRequest =
  | { kind: "recaptcha-v2"; siteKey: string; pageUrl: string; invisible?: boolean; timeoutMs?: number }
  | { kind: "recaptcha-v3"; siteKey: string; pageUrl: string; action?: string; minScore?: number; timeoutMs?: number; invisible?: never };
/**
 * Nozl's own 2captcha account (Jamie, 22/10/26): one key for the platform, set in the console; a company never sees
 * one. Each solve is counted against this company and module. `available()` is false until the key is set (fall back
 * to the company's own key meanwhile). `solve` throws (`code`: not-set-up, limit, credit, key, refused, unsolved,
 * network) when it can't answer; it takes about 20 to 60 seconds.
 */
export interface Captcha {
  available(): boolean;
  solve(req: CaptchaRequest): Promise<{ token: string }>;
}

/** One company on the Companies House register (public data), as `ctx.companies.lookup` gives it. Dates YYYY-MM-DD. */
export interface CompanyRecord {
  number: string;
  name: string;
  /** "active", "dissolved", "liquidation", "administration", "receivership", "voluntary-arrangement", … */
  status: string;
  type: string;
  incorporated: string | null;
  ceased: string | null;
  /** The registered office, on one line. */
  address: string;
  postcode: string | null;
  sic: string[];
  accountsDue: string | null;
  accountsOverdue: boolean;
  confirmationDue: string | null;
  confirmationOverdue: boolean;
  /** The register shows an insolvency case, now or in the past. */
  insolvency: boolean;
  link: string;
}

/**
 * Companies House on Nozl's own key (Platform keys → Companies House). `lookup` takes a company number ("1234567",
 * "SC123456") and gives the register's record, or null when there's no such company. Throws a plain Error when the
 * register can't be asked (no key, refused, down). Answers are kept for a day.
 */
export interface Companies {
  available(): boolean;
  lookup(number: string): Promise<CompanyRecord | null>;
}

/** One road incident from TomTom's Incident Details v5, as `ctx.traffic.incidents` gives it (TomTom's own shape). */
export interface TrafficIncident {
  type?: string;
  geometry?: { type?: string; coordinates?: unknown };
  properties?: {
    /** 0 unknown, 1 accident, 2 fog, 3 dangerous conditions, 4 rain, 5 ice, 6 jam, 7 lane closed, 8 road closed, 9 roadworks, 10 wind, 11 flooding, 14 broken-down vehicle. */
    iconCategory?: number;
    /** 0 unknown, 1 minor, 2 moderate, 3 major, 4 undefined (closures, roadworks). */
    magnitudeOfDelay?: number;
    events?: { description?: string; code?: number }[] | null;
    startTime?: string | null; endTime?: string | null;
    from?: string | null; to?: string | null;
    /** Metres. */ length?: number | null;
    /** Seconds of delay. */ delay?: number | null;
    roadNumbers?: string[] | null;
  };
}

/**
 * Live road incidents on Nozl's own TomTom key (Platform keys → TomTom traffic; free tier, shared by every company).
 * `bbox` is [minLng, minLat, maxLng, maxLat], at most 10,000 km². Gives the incidents present now. The same box asked
 * again within 5 minutes gets the kept answer; once Nozl's look-ups for the day are used it throws a plain Error, as
 * it does with no key, a refusal or TomTom down. Read on a job and keep the answer: never per screen or page view.
 */
/** An outside system a company can switch on in the set-up tool (manifest `integrations`). */
export interface IntegrationDecl {
  /** Unique across Nozl, never changes (e.g. "ready2wash"). Also its logo's key. */
  id: string;
  name: string;
  /** What it brings in, in plain words. */
  what: string;
  /** A site-by-site thing (a car wash, a loyalty scheme): the site service it is, and the tool asks which sites. */
  service?: SiteService;
  /** Its set-up steps (`<module>/<item>`), left out of the checklist while it's switched off. */
  setup: string[];
  /** Only offered when a site matches (e.g. `{ fuel: true }`). */
  when?: SiteCondition;
}

/** One of a module's set-up questions (manifest `questions`). */
export interface ModuleQuestion {
  /** Unique within the module, e.g. "missed-price-change". */
  id: string;
  /** The question, in plain words: "When a site misses a price change…". */
  ask: string;
  help?: string;
  options: { value: string; label: string; help?: string }[];
  /** Only asked when at least one of the company's sites matches (e.g. `{ operatingModel: ["commission"] }`, `{ fuel: true }`). */
  when?: SiteCondition;
}

/**
 * THE COMPANY PROFILE (29/10/26): what the set-up tool asks first, and what every module's defaults are worked out
 * from. Company answers are kept by the platform; each site's come from Sites (how it's run, fuel, tills, wholesaler,
 * what it has). Null = not answered yet.
 */
export interface CompanyProfile {
  /** A head office back office that holds the product file and sends prices to the sites. */
  headOffice: boolean | null;
  /** Who sets shop prices: centrally (head office) or each site in its own back office. */
  shopPrices: "central" | "site" | null;
  sites: {
    code: string; name: string; kind: "site" | "head_office"; model: OperatingModel | null; fuel: boolean | null;
    epos: string | null; wholesaler: string | null; services: SiteService[] | null;
  }[];
}

/** Whether a site's day can be restored again from the kept backup (see `ModuleServices.restore`). */
export interface RestoreCheck {
  ok: boolean;
  system: "cbe" | "vme" | null;
  /** The day the kept backup holds exactly (its own trading day), or null when none is kept. */
  keptDay: string | null;
  /** True when `day` is the kept backup's own day: everything for it is in the backup. */
  exact: boolean;
  /** Plain words for the person: what will happen, or why it can't. */
  why: string;
  /** A run for this site and day already waiting or running. */
  pending: number | null;
}
export interface RestoreRun { id: number; status: "queued" | "running" | "done" | "partial" | "failed"; message: string | null; reports: { report: string; day: string; status: string; message: string }[] }
export interface Restore {
  check(site: string, day: string): Promise<RestoreCheck>;
  /** Queue it. `reports`: the old query names (e.g. "CashOfficeAnalysis"); default every daily report. `by`: who asked. */
  again(site: string, day: string, opts?: { reports?: string[]; by?: string }): Promise<RestoreCheck & { id: number | null }>;
  status(id: number): Promise<RestoreRun | null>;
}

export interface Traffic {
  available(): boolean;
  incidents(q: { bbox: [number, number, number, number]; language?: string }): Promise<TrafficIncident[]>;
  /**
   * How the road nearest a point is flowing now (TomTom Flow Segment Data): speeds in mph. One look-up per point (kept
   * 5 minutes). Null when TomTom has no road there.
   */
  flow(q: { lat: number; lng: number }): Promise<TrafficFlow | null>;
  /**
   * Drive times now from one place to up to 6 others, with live traffic (TomTom Routing). `vehicle` defaults to
   * "truck" (lorry routes: weight limits, low bridges, bans). One look-up per destination (kept 5 minutes); a
   * destination TomTom can't route to comes back with `error` and the rest still answer.
   */
  driveTimes(q: { from: { lat: number; lng: number }; to: TrafficPlace[]; vehicle?: "car" | "truck" }): Promise<DriveTime[]>;
  /**
   * A live traffic map centred on a point: a `width` × `height` window (`cols` × `rows` tiles' worth, default 4 × 3,
   * zoom 11) and the 256-pixel tiles that cover it, each placed at `left`/`top` pixels in the window (some partly
   * outside: clip with overflow hidden). Each tile has a base map and a traffic layer to draw on top. The addresses are Nozl's own (the key
   * never reaches the browser), signed for this company and good for a day, so a public screen can use them. No
   * look-up is made here; tiles are counted against TomTom's separate (much larger) map allowance as they're drawn.
   * The point is always the window's centre (`x`, `y`). Show `attribution` on the map.
   */
  map(q: { lat: number; lng: number; zoom?: number; cols?: number; rows?: number; style?: "night" | "main" }): TrafficMap;
}
export interface TrafficPlace { name: string; lat: number; lng: number }
export interface TrafficFlow {
  /** TomTom's road class: 0 motorway … 6 local road. */
  roadClass: number;
  currentMph: number;
  normalMph: number;
  /** currentMph / normalMph, 0–1 (1 = flowing normally). */
  ratio: number;
  closed: boolean;
  /** TomTom's confidence in the reading, 0–1. */
  confidence: number;
}
export interface DriveTime {
  name: string;
  /** null with `error` when this one couldn't be routed. */
  miles: number | null;
  minutes: number | null;
  /** Minutes added by traffic now (0 when clear). */
  delayMinutes: number | null;
  /** Minutes with no traffic. */
  normalMinutes: number | null;
  error?: string;
}
export interface TrafficMap {
  zoom: number;
  size: 256;
  width: number;
  height: number;
  tiles: { left: number; top: number; base: string; traffic: string }[];
  x: number;
  y: number;
  attribution: string;
}

/** What's worth a warning on a company's record, in plain words (empty when all is well). */
export function companyWarnings(c: CompanyRecord): string[] {
  const out: string[] = [];
  const day = (d: string | null) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(2, 4)}` : "");
  if (c.status !== "active") out.push(c.status === "dissolved" ? `dissolved${c.ceased ? ` on ${day(c.ceased)}` : ""}` : `in ${c.status.replace(/-/g, " ")}`);
  else if (c.insolvency) out.push("has an insolvency case on the register (now or in the past)");
  if (c.status === "active" && c.accountsOverdue) out.push(`accounts overdue${c.accountsDue ? ` (due ${day(c.accountsDue)})` : ""}`);
  if (c.status === "active" && c.confirmationOverdue) out.push(`confirmation statement overdue${c.confirmationDue ? ` (due ${day(c.confirmationDue)})` : ""}`);
  return out;
}

/** Shared by routes, jobs, health checks and self-tests. */
export interface ModuleServices {
  figures: Figures;
  tasks: Tasks;
  secrets: ModuleSecrets;
  market: Market;
  ai: Ai;
  /** Captcha answering on Nozl's own 2captcha account, counted per company (see `Captcha`). */
  captcha: Captcha;
  /** Companies House look-ups on Nozl's own key (see `Companies`). */
  companies: Companies;
  /** TomTom road incidents on Nozl's own key (see `Traffic`). */
  traffic: Traffic;
  /**
   * Ask another module for something it offers (`services` in its server half), e.g. bonuses asking budgets
   * how a site did against target. Returns null when that module isn't switched on for this company.
   */
  call<T = unknown>(moduleId: string, service: string, args?: Record<string, unknown>): Promise<T | null>;
  /** The modules switched on for this company (ids). */
  modules(): Promise<string[]>;
  /** Every module's yes / no switches for this company, as "<module>.<switch>" → on (see ServerModule.features). */
  features(): Promise<Record<string, boolean>>;
  /**
   * The tills, per site, as the head office PC's Nozl Bridge sees CBE's send flag (Platform Core 29/10/26): whether
   * price changes are waiting to go (`sending`), since when, and when changes last reached the tills (`sentAt`, within
   * half a minute). Empty without a bridge. Head office (no tills) is marked. Use it for "Reached the tills HH:MM":
   * a change sent at T has reached a site's tills once its `sentAt` is after T.
   */
  tills(): Promise<TillSend[]>;
  /**
   * The company's connection lights, as Settings → Connections shows them (27/10/26, for Monitor's "Data and
   * connections" panel): each outside source's colour and plain reason, read-only. A light nobody has checked for 45
   * minutes is amber at best (the background worker may have stopped). Lights that are "off" are left out.
   */
  connections(): Promise<ConnectionLight[]>;
  /**
   * The products this company has taken (ids, e.g. "core", "insights-plus"), while they're being set up, live or
   * ending: what a module checks before a paid part (Insights+ gates its AI on "insights-plus").
   */
  products(): Promise<string[]>;
  /** How each site is run on a day (commission / operator / company / null = not set). */
  siteModels(day?: string): Promise<Record<string, OperatingModel | null>>;
  /** What each site has (Ready2Wash, Costa…), as answered in Setup; null = not answered yet. */
  siteServices(): Promise<Record<string, SiteService[] | null>>;
  /**
   * Keep a file this module received another way (an email attachment, a fetched document). Returns its id.
   * `private`: encrypted on disk and never served by the general file link; the module serves it with its own checks.
   */
  /**
   * Keep a file for this company (encrypted with its key; its type is checked from its bytes, 50 MB at most). Throws
   * (an HttpError, status 413 or 415) when a file is refused, so callers taking outside files should catch it.
   * `source` defaults to "email" for ref "email", otherwise "system"; `category` defaults from the module (filestore.ts).
   */
  storeFile(f: { name: string; mime: string; data: Uint8Array; site?: string | null; ref?: string | null; private?: boolean; source?: "email" | "agent" | "import" | "old-cloud" | "system"; category?: string }): Promise<string>;
  /** The contents of a file uploaded to THIS module (any site), decrypted. Null if it isn't this module's. */
  readFile(id: string): Promise<{ name: string; mime: string; site: string | null; size: number; ref: string | null; data: Uint8Array } | null>;
  /** Delete a file of THIS module for good (retention, a replaced document). */
  removeFile(id: string): Promise<void>;
  /**
   * Field encryption with the platform vault (AES-256-GCM, key never in a database): for personal data such as
   * NI numbers, dates of birth, addresses and bank details. `open` returns null for anything it can't read.
   */
  vault: { seal(plain: string): string; open(sealed: string | null | undefined): string | null };
  /** UK bank holidays between two dates (England & Wales unless a region is given). */
  bankHolidays(from: string, to: string, region?: "england-and-wales" | "scotland" | "northern-ireland"): Promise<{ day: string; title: string }[]>;
  /** A stock photo for a few words (training slides). Null when none is found or the service isn't set up. */
  stockImage(query: string): Promise<{ data: Uint8Array; mime: string; credit: string } | null>;
  /**
   * Product pictures, kept once per barcode (leading zeros dropped, so a till's padded code and a wholesaler's EAN
   * meet) and SHARED ACROSS NOZL (26/10/26): a feed's picture goes into Nozl's one library and every company sees it;
   * one chosen by hand ("upload") stays the company's own and always wins. `get` returns the picture; `has` which
   * codes have one; `put` keeps a file of THIS module as a product's picture ("wholesaler": into the shared library,
   * and the module's copy is binned; "upload": the company's own; null file: looked, there isn't one). `putShared`
   * puts bytes straight into the library (no company file first). In SQL, "has a picture" is
   * `NOT missing AND (file_id IS NOT NULL OR shared)` on `product_images`.
   */
  productImages: {
    get(code: string): Promise<{ mime: string; data: Uint8Array } | null>;
    has(codes: string[]): Promise<Set<string>>;
    put(code: string, fileId: string | null, source: "wholesaler" | "upload", byName?: string): Promise<void>;
    putShared(code: string, img: { data: Uint8Array } | null, source: string): Promise<boolean>;
  };
  /** Queue one of this module's jobs to run in the background (now, or at `runAt`). Returns the job number. */
  queue(job: string, payload?: Record<string, unknown>, runAt?: Date): Promise<number>;
  /**
   * The restore worker's kept backup, for a module offering "Restore again" on a site's day (29/10/26, Cash office):
   * `check` says whether that day can be made again and what to expect, `again` queues it (Console → Restore worker's
   * Run days again, for that one site and day) and `status` follows it. Only the newest backup of a site is kept, so
   * the day it holds exactly is its own (`keptDay`); an earlier day is tried from it and a report whose day the backup
   * no longer holds is not sent (nothing already here is replaced). CBE and VME tills only.
   */
  restore: Restore;
  /**
   * ONE NAME PER BARCODE (29/10/26): the newest name Nozl has for each barcode (head office's product file first,
   * else the sites' own lists, newest first), keyed by the barcode with leading zeros dropped. Show this name for a
   * product on every page, not the description stored on a sales or delivery line, so a product renamed in the back
   * office reads the same everywhere from the next backup. No codes = every barcode.
   */
  productNames(codes?: string[]): Promise<Map<string, string>>;
  /**
   * THE OLD CLOUD LINK (29/10/26): true only for a company still moving from the old Tankerford Cloud (Tankerford,
   * until switch-over); false for every other company, and for everyone once it's retired in the console. When false a
   * module shows nothing about the old Cloud (banners, "until switch-over" notes, compare-with-old views), offers
   * nothing that reads or writes it, and runs no job against it. Mark such menu entries `oldCloud: true`.
   */
  oldCloud(): Promise<boolean>;
  /** This company's Nozl mailbox for this module: addresses it forwards emails to, and the emails that arrived. */
  mailbox: Mailbox;
  /**
   * Nozl's own keys for shared services (Nozl console → Platform keys, one per server), not a company's: the route
   * service for road distances and the keyed map background. Null when Nozl hasn't set one up.
   */
  nozlKeys: {
    roads(): { provider: "google" | "openrouteservice"; key: string } | null;
    mapTiles(): { provider: "maptiler" | "stadia"; key: string; urlTemplate: string | null } | null;
  };
  /**
   * This module's Setup steps. Call `recheck` after a module page saves something a step checks (e.g. a bulk "link
   * every site" page), so the checklist turns green straight away. A site item without `site` rechecks every site.
   */
  setup: { recheck(item: string, site?: string | null): Promise<void> };
}

export interface RequestContext extends ModuleServices {
  tenant: { id: string; slug: string; name: string };
  user: { id: string; email: string; name: string };
  /** The signed-in user's company database. The only database a module may touch. */
  db: TenantDb;
  /** Sites the user may act on; null = all sites of the company. */
  siteScope: string[] | null;
  /** The caller's IP address (the first address before Nozl's proxy), e.g. for an access log. */
  ip: string;
  can(permission: string): boolean;
  params: Record<string, string>;
  query: Record<string, string | undefined>;
  body: unknown;
  /** Files the user uploaded (via POST /api/files) for this module. */
  files: ModuleFiles;
  /** Send in-app notifications (and later email/push) to people in this company. */
  notify(userIds: string[], n: Notice): Promise<void>;
  /** People in this company (active logins), for pickers like "assign to". */
  people(): Promise<Person[]>;
  /** Record something important in the company's audit log (who and when are added automatically). */
  audit(action: string, detail?: Record<string, unknown>): Promise<void>;
  /** How this person signed in: with a second factor (authenticator, passkey), and confirmed it's them recently. */
  session: { mfa: boolean; steppedUp: boolean };
}

export interface FileInfo { id: string; name: string; mime: string; size: number; site: string | null; url: string }

export interface ModuleFiles {
  /**
   * Attach uploaded files to something in this module (e.g. a maintenance job). Only files uploaded to
   * THIS module, by THIS user, not yet attached, and for an allowed site are accepted; anything else throws.
   */
  attach(ids: string[], ref: string, opts?: { private?: boolean }): Promise<FileInfo[]>;
  /** Details of files already attached to `ref` in this module. */
  list(ref: string): Promise<FileInfo[]>;
}

export interface Notice {
  title: string; body?: string; link?: string; level?: "info" | "warning" | "critical";
  /**
   * What it's about, for each person's "How I hear about things" (push and email per topic): a few words after the
   * module, e.g. "price-summary" → "fuel.price-summary". Unset: the module itself.
   */
  topic?: string;
}

export interface Person {
  id: string; name: string; email: string; roles: string[]; sites: string[] | null;
  /** Their manager (one level up), who approves their leave, expenses and timesheets. */
  reportsTo?: string | null;
  /** Their level (100 company admin … 10 staff). */
  rank?: number;
  /** Everything their roles allow ("*" = everything). */
  permissions: string[];
}

/** Does this person hold a permission (with "*" and "<module>.*" wildcards)? */
export function personCan(p: Pick<Person, "permissions">, permission: string): boolean {
  const mod = permission.split(".")[0];
  return p.permissions.includes("*") || p.permissions.includes(permission) || p.permissions.includes(`${mod}.*`);
}

/** Does this person cover a site? (null sites = all sites) */
export const personCovers = (p: Pick<Person, "sites">, site: string) => p.sites === null || p.sites.includes(site);

/* ---------------- background jobs ---------------- */

export interface JobContext extends ModuleServices {
  tenant: { id: string; slug: string; name: string };
  db: TenantDb;
  payload: Record<string, unknown>;
  notify(userIds: string[], n: Notice): Promise<void>;
  people(): Promise<Person[]>;
}

/** What a public (signed-out) route gets: the company from the web address, its database and the query string. */
export interface PublicContext {
  tenant: { id: string; slug: string; name: string };
  db: TenantDb;
  query: Record<string, string | undefined>;
  /** The caller's address (for a screen's "last seen"). */
  ip: string;
  /** The shared weather forecast. */
  market: Pick<Market, "weather">;
  /** A file of THIS module (to serve it with the module's own checks, e.g. a screen's adverts). */
  readFile: ModuleServices["readFile"];
  /** Product pictures by barcode (e.g. for the TV screens). */
  productImage: ModuleServices["productImages"]["get"];
  /** Ask another module for something it offers, as from a route (null when it isn't switched on). Return only what's safe to show the public. */
  call: ModuleServices["call"];
  /**
   * The live traffic map for a public screen (26/10/26, Modules for the TV traffic slide): only `available` and `map`,
   * which builds signed tile addresses and makes no TomTom look-up, so a public request can never spend the day's
   * look-ups (incidents, flow and drive times are read on a job and kept).
   */
  traffic: Pick<Traffic, "available" | "map">;
  /** The JSON body, for a POST public route (a key written "POST /path"); undefined for GET. */
  body?: unknown;
  /** The caller's browser as it describes itself (cut to 200 characters), e.g. for a list of a screen's TVs. */
  userAgent: string;
  /**
   * Cookies for this module's public routes only (27/10/26, Monitor's remembered TVs): httpOnly, SameSite=Strict,
   * HTTPS-only on sandbox and live, sent only to /api/public/<module id>, and signed with a key derived from the vault
   * for this module and company, so `get` returns null for one that was altered or set for another company. Keep only
   * a random id in one (checked against the module's own table, so it can be revoked), never data.
   */
  cookies: { get(name: string): string | null; set(name: string, value: string, days: number): void; clear(name: string): void };
  /** Write to the company's audit trail as "<module id>.<action>", with no person (the caller isn't signed in). */
  audit(action: string, detail?: Record<string, unknown>): Promise<void>;
}

/* ---------------- connections (outside sources) ---------------- */

/** green working, amber needs a look, red broken, off not set up for this company. */
export type ConnectionStatus = "green" | "amber" | "red" | "off";
export interface ConnectionResult {
  status: ConnectionStatus;
  /** One plain line: what was found ("Logged in: 11 sites", "No email for 3 days"). */
  detail: string;
  /** When data last arrived from it (ISO), if that's known. */
  lastData?: string | null;
}
/**
 * An outside source a module reads from or sends to: a portal it logs in to, an API, a mailbox, a feed. Each one is
 * listed on Connections with a light, so a broken one is fixed straight away.
 */
/** A company's light for one connection (see ModuleServices.connections). `since`: when it last changed colour. */
export interface ConnectionLight { key: string; name: string; provider: string; module: string | null; status: ConnectionStatus; detail: string; since: string | null; lastData: string | null }
/** One run in a module's own log for a connection (see ConnectorDecl.runs). `at` is an ISO time. */
/** One site's tills as the Nozl Bridge reports them (ModuleServices.tills). Times are ISO. */
export interface TillSend { site: string; headOffice: boolean; sending: boolean; since: string; queuedAt: string | null; sentAt: string | null; heardAt: string }

export interface ConnectorRun { at: string; status: "ok" | "partial" | "failed" | "running"; title: string; detail: string; site?: string | null }

export interface ConnectorDecl {
  /** Unique across Nozl, never changes (e.g. "sm365", "nayax-mailbox"). */
  key: string;
  name: string;
  /** Who runs the other end (e.g. "Edge (SM365)", "Nayax", "GOV.UK"). */
  provider: string;
  /** What it brings in or sends out, in plain words. */
  what: string;
  how: "portal login" | "api" | "mailbox" | "feed" | "rss" | "upload" | "messaging";
  direction: "in" | "out" | "both";
  /** How often it runs, in words ("Every 15 minutes"). */
  cadence: string;
  /** The setup item (in this module) holding its login, so the page can link straight to it. */
  setupItem?: string;
  /** Live test: log in, connect or call it. Run every hour and on "Test now". */
  test?(ctx: JobContext): Promise<ConnectionResult>;
  /** Is data still arriving? Cheap (our own tables). Run every 15 minutes. */
  fresh?(ctx: JobContext): Promise<ConnectionResult>;
  /** The module's job that brings the data in: "Fetch now" on the connection queues it straight away (25/10/26). */
  job?: string;
  /**
   * The module's own run log for this connection, newest first, at most 20 (e.g. each Co-op portal run): shown as
   * "Recent runs" on the connection's sheet in Settings → Connections (26/10/26).
   */
  runs?(ctx: JobContext): Promise<ConnectorRun[]>;
  /**
   * "Bring in history" (Jamie 29/10/26): a new company's past data comes from the integration itself, not from
   * forwarded emails. Optional. The platform draws the button on the connection's sheet (Setup → Connections, and
   * <BringInHistory> wherever a module shows its connection), asks for a start date ("fetch") or a file ("upload",
   * sent to POST /api/files for this module first), shows `preview`, and on Confirm runs `apply` one month at a time
   * as a job, oldest first, with progress; a stopped run carries on from the month it stopped at.
   */
  history?: ConnectorHistory;
}

/** What "Bring in history" asks of a connection (ConnectorDecl.history). */
export interface ConnectorHistory {
  /** "fetch": read from the source for a date range (a portal or API). "upload": a file exported from the source. */
  kind: "fetch" | "upload";
  /** Plain words shown before anything is picked: for "upload", which report to export and how; for "fetch", what it reads. */
  howTo: string;
  /** How far back it can go (fetch: the earliest start offered). Default 24. */
  maxMonths?: number;
  /** Upload: file types the picker offers (".csv", ".xlsx"…). */
  accept?: string[];
  /** What a run would bring in, without changing anything. For "upload" the range is the file's own; `from`/`to` narrow it. */
  preview(ctx: JobContext, req: HistoryRequest): Promise<HistoryPreview>;
  /**
   * Bring in one month (the part of `month` inside from…to). Must be safe to run twice: days already loaded are
   * replaced or skipped, never doubled. Throw to stop the run with that message (it can be started again from there).
   */
  apply(ctx: JobContext, req: HistoryRequest & { month: string }): Promise<HistoryMonthResult>;
}

/** The range and the file: `from`/`to` YYYY-MM-DD (UK days, inclusive); `fileId` an upload to this module. */
export interface HistoryRequest { from?: string; to?: string; fileId?: string }

export interface HistoryPreview {
  /** The range it would bring in (YYYY-MM-DD). */
  from: string; to: string;
  /** Days in that range with data. */
  days: number;
  /** Rows (sales, transactions…) it would bring in. */
  total: number;
  /** Rows it can't place (e.g. a machine or site it doesn't know), with why in `notes`. */
  unplaced: number;
  /** Days in the range Nozl already has for this connection (they're replaced, never doubled). */
  existingDays: number;
  /** Anything worth saying before Confirm, in plain words. */
  notes: string[];
}

export interface HistoryMonthResult { days: number; rows: number; unplaced?: number; notes?: string[] }

/** The saved values of a company-wide setup item (not its secrets), e.g. a mailbox's server and username. */
export async function setupValues(db: Queryable, module: string, item: string): Promise<Record<string, string>> {
  const [row] = await db.query<{ values: Record<string, string> }>(`SELECT "values" FROM setup_state WHERE module = $1 AND item = $2 AND site_code = ''`, [module, item]);
  return row?.values ?? {};
}
/** Save a company-wide setup item's values (as the checklist's form does); its check runs again on the next refresh. */
export async function saveSetupValues(db: Queryable, module: string, item: string, values: Record<string, unknown>): Promise<void> {
  await db.query(
    `INSERT INTO setup_state (module, item, site_code, "values") VALUES ($1, $2, '', $3)
     ON CONFLICT (module, item, site_code) DO UPDATE SET "values" = EXCLUDED."values", checked_at = NULL, updated_at = now()`,
    [module, item, JSON.stringify(values)]);
}

/**
 * One setting the set-up tool's answers decide (`questions.apply`, docs/onboarding.md "The set-up tool").
 *  - `key`: unique within the module and never renamed ("hold_until_release", "cadence:ELM").
 *  - `want`: what the answers say it should be; `now`: what it is (null / undefined = nothing set at all).
 *  - `fresh`: nobody has chosen it yet (the module is new to the company and the setting is as it started), so the
 *    answers may set it. A company already running (Tankerford) is never fresh: only an empty value is filled.
 *  - `write`: puts `want` into the module's own settings.
 */
export interface SetupSetting<T = unknown> {
  key: string;
  want: T;
  now: T | null | undefined;
  fresh?: boolean;
  write(value: T): Promise<void>;
}

/** What the set-up questions wrote (`set`) and the settings a person has since changed by hand (`byHand`). */
interface SetupMarks { set: Record<string, unknown>; byHand: string[] }
const SAME = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Write the set-up answers into a module's settings, leaving a person's choices alone (29/10/26). A setting is written
 * only when the questions set it last time and it hasn't been changed since, or when nothing has been chosen yet
 * (empty, or `fresh`). A setting changed by hand on the module's own page is the person's from then on: it is never
 * written by the questions again. The marks are kept in setup_state (`<module>` / `set-up-answers`).
 * Returns the keys written and the keys left as they were.
 */
export async function applySetupAnswers(db: Queryable, module: string, settings: SetupSetting[]): Promise<{ written: string[]; kept: string[] }> {
  const [row] = await db.query<{ values: Partial<SetupMarks> }>(`SELECT "values" FROM setup_state WHERE module = $1 AND item = 'set-up-answers' AND site_code = ''`, [module]);
  const marks: SetupMarks = { set: { ...(row?.values?.set ?? {}) }, byHand: [...(row?.values?.byHand ?? [])] };
  const written: string[] = [], kept: string[] = [];
  for (const s of settings) {
    if (marks.byHand.includes(s.key)) { kept.push(s.key); continue; }
    const ours = Object.prototype.hasOwnProperty.call(marks.set, s.key);
    if (ours && !SAME(s.now, marks.set[s.key])) {
      // Changed on the module's own page since the questions set it: the person's now.
      marks.byHand.push(s.key); delete marks.set[s.key]; kept.push(s.key); continue;
    }
    if (!ours && !(s.now === null || s.now === undefined || s.fresh)) { kept.push(s.key); continue; }
    if (!SAME(s.now, s.want)) await s.write(s.want);
    marks.set[s.key] = s.want;
    written.push(s.key);
  }
  await db.query(
    `INSERT INTO setup_state (module, item, site_code, "values", status, detail, checked_at) VALUES ($1, 'set-up-answers', '', $2, 'green', 'Settings from the set-up questions', now())
     ON CONFLICT (module, item, site_code) DO UPDATE SET "values" = EXCLUDED."values", status = 'green', checked_at = now(), updated_at = now()`,
    [module, JSON.stringify(marks)]);
  return { written, kept };
}

/** A barcode as one key everywhere: digits only, leading zeros dropped ("0000040608143" and "40608143" meet). */
export const gtinKey = (code: string | null | undefined): string => String(code ?? "").replace(/\D/g, "").replace(/^0+/, "");
/** A barcode as head office's files want it: digits, left-padded to 13. */
export const pad13 = (code: string | null | undefined): string => { const d = String(code ?? "").replace(/\D/g, ""); return d.length >= 13 ? d : d.padStart(13, "0"); };

/** "5 min ago", "3 h ago", "2 days ago" (or "never"). */
export function ago(iso: string | null | undefined): string {
  if (!iso) return "never";
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 48 * 60 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`;
}

export type JobHandler = (ctx: JobContext) => Promise<void | string>;

/** Run a module job on a timetable for every company that has the module. Times are UK time. */
export interface Schedule {
  job: string;
  /** "daily" at a time, or "every" N minutes. */
  daily?: string;
  everyMinutes?: number;
}

export type Handler = (ctx: RequestContext) => Promise<unknown> | unknown;

export interface RouteOptions {
  /** REQUIRED. Permission key in this module. The platform refuses to mount a route without one. */
  permission: string;
  /** Validate the JSON body; invalid bodies get a 400 before the handler runs. */
  body?: z.ZodType;
  /**
   * Sensitive: the person must have confirmed it's them in the last 10 minutes (Face ID, their PIN on a trusted
   * device, or password + code). The app asks and retries automatically. Use for reopening signed-off figures,
   * bank details, overrides that change what someone is paid, bulk deletes.
   */
  stepUp?: boolean;
}

export interface Router {
  get(path: string, options: RouteOptions, handler: Handler): void;
  post(path: string, options: RouteOptions, handler: Handler): void;
  put(path: string, options: RouteOptions, handler: Handler): void;
  delete(path: string, options: RouteOptions, handler: Handler): void;
}

export interface ServerModule {
  manifest: ModuleManifest;
  /** True if this module keeps numbered company-database migrations in modules/<id>/migrations (0001_name.sql …). */
  tenantMigrations?: boolean;
  routes?: (r: Router) => void;
  /** One handler per manifest setup item id. The platform refuses to load a module with a setup item that has no check. */
  setup?: Record<string, SetupHandlers>;
  /** How this module pulls in old data when a company or site is onboarded. */
  history?: HistoryImporter;
  /** Background jobs this module can run, by name. Queue one from a route with ctx… (future) or via schedules. */
  jobs?: Record<string, JobHandler>;
  schedules?: Schedule[];
  /**
   * Is this module working for this company right now? Called by the platform's health monitor (Control Centre).
   * Report when data last arrived and anything wrong in plain English. Optional: without it, health is judged
   * from setup checks and job failures only.
   */
  health?: (ctx: JobContext) => Promise<ModuleHealth>;
  /** Things other modules may ask this one for (see ModuleServices.call). */
  services?: Record<string, (ctx: JobContext, args: Record<string, unknown>) => Promise<unknown>>;
  /**
   * The module's own yes / no switches for this company (e.g. fuel: `{ pricing: true, margins: false }`). A menu entry
   * or card with `feature: "margins"` leaves the menu and "Add cards" while it's false. Asked when a session starts
   * (kept a minute); a switch missing from the answer counts as on.
   */
  features?: (ctx: JobContext) => Promise<Record<string, boolean>>;
  /**
   * The set-up tool's questions (manifest `questions`): `suggest` gives an answer for each question from the company
   * profile (shown ready-picked); `apply` writes the answers into the module's settings. `apply` runs when the answers
   * are saved and again whenever the company profile changes (a site starts selling fuel, a site is added), so it must
   * leave alone any setting a person has changed on the module's own settings page since.
   */
  questions?: {
    suggest(ctx: JobContext, profile: CompanyProfile): Promise<Record<string, string>> | Record<string, string>;
    apply(ctx: JobContext, answers: Record<string, string>, profile: CompanyProfile): Promise<void>;
  };
  /**
   * Read-only pages anyone may open without signing in (e.g. checking a training certificate is real), served at
   * GET /api/public/<module id><path> for the company the address belongs to. Rate-limited. Return only what is
   * safe for the public to see. A key written "POST /path" is a POST with a small JSON body (`ctx.body`, 8 KB at most),
   * e.g. a TV entering its access code; the platform's same-site check for changes applies to it.
   */
  publicRoutes?: Record<string, (ctx: PublicContext) => Promise<unknown>>;
  /** The outside sources this module reads from or sends to (listed on Connections with a live test). */
  connectors?: ConnectorDecl[];
  /**
   * Nozl mailbox: run a job of this module the moment an email arrives at one of its addresses, by key (e.g.
   * { deliveries: "read-mail" }). The job gets { mailboxKey, messageId } and reads it with ctx.mailbox.
   */
  mailboxJobs?: Record<string, string>;
  /** Requests a minute each address may make to this module's public routes (default 30; screens that poll need more). */
  publicPerMinute?: number;
  /** Dashboard cards: data for each widget declared in the manifest. */
  widgets?: Record<string, (ctx: JobContext, q: WidgetQuery) => Promise<WidgetData>>;
  /**
   * Tasks: bring this module's tasks in step with its data (raise what's due, clear what's done). Run by the Tasks
   * engine every 10 minutes and on "Run now". Modules also raise/clear straight away when something changes.
   */
  syncTasks?: (ctx: JobContext) => Promise<void>;
  /**
   * Tasks: may this task be signed off now? Return a message to refuse (e.g. "2 days still to agree: Mon 5, Tue 6"),
   * or null to allow. The task clears itself later when the work is done.
   */
  taskGuard?: (ctx: JobContext, task: { type: string; key: string; site: string | null; facts: TaskFacts }) => Promise<string | null>;
  /**
   * The Module Lab runs this against a copy of EVERY live company's data (inside a transaction that is rolled
   * back) before the module can be released: exercise the module the way that company would, throw on anything
   * wrong, and return plain-English notes ("3 commission sites found, statements worked out").
   */
  selfTest?: (ctx: JobContext & { sites: { code: string; name: string; model: OperatingModel | null }[] }) => Promise<string[]>;
}

export interface ModuleHealth {
  status: "green" | "amber" | "red";
  detail: string;
  /** ISO time the module last received or produced real data (a till import, a job raised…). */
  lastDataAt?: string | null;
}

/* ---------------- history import (onboarding backfill) ---------------- */

/**
 * During onboarding the company picks how far back to bring data in (e.g. 3 months, 1 year, everything).
 * Each module that holds historical data says what it can import and imports one chunk at a time.
 * The platform splits the range into monthly chunks per site, NEWEST FIRST (so recent months are usable
 * straight away), queues them as background jobs, and ticks each one green on the onboarding screen.
 */
export interface HistoryImporter {
  /** Shown in the wizard, e.g. "Sales, waste and stock from your EPOS". */
  label: string;
  /** Where it comes from, e.g. "Nightly EPOS backup", "SM365", "Nisa portal", "Tankerford Cloud (legacy)". */
  source: string;
  /** Earliest date the source can provide for this site, if it can tell (so the wizard can offer "everything"). */
  earliest?(ctx: SetupContext): Promise<string | null>;
  /** Rough size for the wizard ("about 2.1m rows, ~40 min"). */
  estimate?(ctx: SetupContext, from: string, to: string): Promise<{ rows: number; minutes: number }>;
  /**
   * Import one chunk. MUST be idempotent (safe to re-run: upsert on natural keys, never duplicate),
   * because chunks are retried and can be re-requested later to extend history further back.
   */
  importChunk(ctx: SetupContext, chunk: { from: string; to: string }): Promise<{ rows: number; detail?: string }>;
}

/**
 * Wrap a router so `fn` runs after every change (POST, PUT, DELETE) succeeds — e.g. to bring the module's tasks up
 * to date straight away. Errors in `fn` never fail the request.
 */
export function afterChanges(r: Router, fn: (ctx: RequestContext) => Promise<unknown>): Router {
  const wrap = (h: Handler): Handler => async (ctx) => { const out = await h(ctx); await fn(ctx).catch(() => undefined); return out; };
  return { get: (p, o, h) => r.get(p, o, h), post: (p, o, h) => r.post(p, o, wrap(h)), put: (p, o, h) => r.put(p, o, wrap(h)), delete: (p, o, h) => r.delete(p, o, wrap(h)) };
}

export function defineServerModule(m: ServerModule): ServerModule {
  for (const item of m.manifest.setup ?? []) {
    if (!m.setup?.[item.id]) throw new Error(`${m.manifest.id}: setup item "${item.id}" has no check`);
  }
  for (const s of m.schedules ?? []) {
    if (!m.jobs?.[s.job]) throw new Error(`${m.manifest.id}: schedule runs unknown job "${s.job}"`);
    if (!s.daily === !s.everyMinutes) throw new Error(`${m.manifest.id}: schedule "${s.job}" needs exactly one of daily or everyMinutes`);
    if (s.daily && !/^([01]\d|2[0-3]):[0-5]\d$/.test(s.daily)) throw new Error(`${m.manifest.id}: schedule "${s.job}" daily must be HH:MM`);
  }
  return m;
}

/** Error a handler can throw to answer with a status code and a plain message. */
/** Return this from a route to send a file (CSV, Excel…) as a download instead of JSON. */
export class FileReply {
  /** `inline`: shown in the browser (a receipt photo, a PDF) rather than downloaded. */
  constructor(
    public readonly name: string, public readonly type: string, public readonly body: string | Uint8Array, public readonly inline = false,
    /** `cache`: a Cache-Control value (default no-store). `ranges`: answer "Range" requests (video on phones needs this). */
    public readonly opts: { cache?: string; ranges?: boolean } = {},
  ) {}
}

export class HttpError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

/* Category roles: what each of the company's groups and departments is for (Products → Categories). */
export * from "./roles.js";
